import Axios from 'axios'
import { InventoryCombination, InventoryCombinationsManifestJson } from '../API/InventoryCombinationsJson'
import { validateInventoryCombinations } from '../API/validation'
import GameScene from '../Components/GameScene/GameScene'
import Level from '../Components/Level'
import Config from '../Containers/Config'
import InventoryContainer from '../Containers/InventoryContainer/InventoryContainer'
import AudioController, { RandomAnswerType } from './AudioController'

class InventoryCombinationsController {
    loadedCombinations: Record<string, InventoryCombination> = {}
    combinationsManifest: InventoryCombinationsManifestJson = {}

    constructor() {
        // WHY: parcel can not load stuff in the correct order :/ so the InventoryActionType is undefined at constructor runtime
        addEventListener('Inventory.added', (ev: Event) => {
            // @ts-ignore -- WHY: CustomEvent
            const itemId = ev.detail.itemId as string

            this.loadCombinations([...InventoryContainer.itemsInInventory, itemId])
        })
    }

    load = async () => {
        const response = await Axios.get(`${Config.ASSET_PATH}inventoryCombinations.json`)

        const manifest: InventoryCombinationsManifestJson = response.data

        if (!validateInventoryCombinations) {
            throw new Error('Inamove::InventoryCombinationsController => could not get validation!')
        }

        if (!validateInventoryCombinations(manifest)) {
            console.error(
                `Inamove::InventoryCombinationsController => Manifest validation failed:`,
                validateInventoryCombinations.errors
            )
            throw new Error()
        }

        const withCorrectUrls = Object.keys(manifest).reduce<InventoryCombinationsManifestJson>(
            (acc, combinationId) => ({
                ...acc,
                [combinationId]: {
                    ...manifest[combinationId],
                    src: manifest[combinationId].src
                        ? Config.INVENTORY_COMBINATION_AUDIO_PATH + manifest[combinationId].src + Config.AUDIO_FILE_EXT
                        : undefined,
                },
            }),
            {}
        )

        this.combinationsManifest = withCorrectUrls
    }

    /**
     * Get all combinations where this item is part of.
     */
    getAllPossibleCombinationsOfItems = (itemIds: Array<string>) => {
        // get all possible combinations from supplied itemIds
        const allCombinationIds = Object.keys(this.combinationsManifest)
        return itemIds.reduce<Array<string>>(
            (possibleCombinations, itemId) => [
                ...possibleCombinations,
                // Why: prevent duplication
                ...allCombinationIds.filter(
                    (combinationId) =>
                        (combinationId.endsWith('+' + itemId) || combinationId.startsWith(itemId + '+')) &&
                        !possibleCombinations.includes(combinationId)
                ),
            ],
            []
        )
    }

    /**
     * Get all combinations where only the given items are part of.
     */
    getPossibleCombinationsOfItems = (itemIds: Array<string>) => {
        const allCombinationIds = Object.keys(this.combinationsManifest)

        return allCombinationIds.filter((combinationId) => {
            const [firstItem, secondItem] = combinationId.split('+')
            return itemIds.includes(firstItem) && itemIds.includes(secondItem)
        })
    }

    loadCombinationsForLevel = (startingItemIds: Level['startingItemIds'], gameScenes: Array<GameScene>) => {
        const takeableItemIds: Array<string> = gameScenes.reduce(
            (acc, gameScene) => [
                ...acc,
                ...Object.keys(gameScene.takeableObjects).map(
                    (takeableObjectId) => gameScene.takeableObjects[takeableObjectId].inventoryItemId
                ),
            ],
            [] as Array<string>
        )

        let possibleCombinations = this.getPossibleCombinationsOfItems([...startingItemIds, ...takeableItemIds])
        let combinationCount = possibleCombinations.length

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const possibleCombinationsWithCombinedItems = this.getPossibleCombinationsOfItems([
                ...startingItemIds,
                ...takeableItemIds,
                ...(possibleCombinations
                    .flatMap((id) => this.combinationsManifest[id].addItems)
                    .filter((id) => id !== undefined) as Array<string>),
            ])

            if (combinationCount < possibleCombinationsWithCombinedItems.length) {
                combinationCount = possibleCombinationsWithCombinedItems.length
                possibleCombinations = possibleCombinationsWithCombinedItems
            } else {
                break
            }
        }

        const loadedCombinations = possibleCombinations
            // do not load combination twice
            .filter((combinationId) => this.loadedCombinations[combinationId] === undefined)
            // load each combination
            .map((combinationId) => {
                const combinationManifest = this.combinationsManifest[combinationId]
                const combination: InventoryCombination = {
                    ...combinationManifest,
                    id: combinationId,
                    audioId: combinationManifest.src && AudioController.add(combinationManifest.src),
                    addItems: combinationManifest.addItems ?? [],
                    removeItems: combinationManifest.removeItems ?? [],
                }

                return combination
            })

        // add newly loaded combinations
        this.loadedCombinations = loadedCombinations.reduce(
            (acc, loadedCombination) => ({
                ...acc,
                [loadedCombination.id]: loadedCombination,
            }),
            this.loadedCombinations
        )
    }

    loadCombinations = async (itemIds: Array<string>) => {
        // TODO: must be recursive as items that are added could also be combined again
        const combinationIdsToLoad = this.getPossibleCombinationsOfItems(itemIds)

        const loadedCombinations = await Promise.all(
            combinationIdsToLoad
                // do not load combination twice
                .filter((combinationId) => this.loadedCombinations[combinationId] === undefined)
                // load each combination
                .map(async (combinationId) => {
                    const combinationManifest = this.combinationsManifest[combinationId]
                    const combination: InventoryCombination = {
                        ...combinationManifest,
                        id: combinationId,
                        audioId: combinationManifest.src && AudioController.add(combinationManifest.src),
                        addItems: combinationManifest.addItems ?? [],
                        removeItems: combinationManifest.removeItems ?? [],
                    }

                    // Why: load all items that could be added
                    await InventoryContainer.loadItems(combination.addItems)

                    return combination
                })
        )

        // add newly loaded combinations
        this.loadedCombinations = loadedCombinations.reduce(
            (acc, loadedCombination) => ({
                ...acc,
                [loadedCombination.id]: loadedCombination,
            }),
            this.loadedCombinations
        )
    }

    /**
     * Build InventoryCombination id from 2 InventoryItem ids.
     * @example
     *  buildInventoryCombinationId('I2', 'I12') // 'I12+I2'
     */
    private buildInventoryCombinationId = (id1: string, id2: string) => [id1, id2].sort().join('+')

    combine = async (id1: string, id2: string) => {
        const combinationId = this.buildInventoryCombinationId(id1, id2)
        const combination = this.loadedCombinations[combinationId]

        if (combination === undefined && !this.combinationsManifest[combinationId]) {
            console.error(`Inamove::InventoryCombinationController => combination not found: ${combinationId}`)
            return await AudioController.playRandomAnswer(RandomAnswerType.NO)
        }

        if (combination === undefined && this.combinationsManifest[combinationId]) {
            console.error(`Inamove::InventoryCombinationController => combination not loaded: ${combinationId}`)
            return
        }

        if (combination !== undefined && (combination.addItems.length > 0 || combination.removeItems.length > 0)) {
            // does remove or add something -> yes
            if (combination.audioId !== undefined) {
                await AudioController.playActionAudio(combination.audioId, combination.text)
            } else {
                await AudioController.playRandomAnswer(RandomAnswerType.YES)
            }
            // add/remove items
            InventoryContainer.removeItems(combination.removeItems)
            await InventoryContainer.addItems(combination.addItems)
        } else {
            // used for combinations that fail but have extra text.
            if (combination !== undefined && combination.audioId !== undefined) {
                await AudioController.playActionAudio(combination.audioId, combination.text)
            } else {
                await AudioController.playRandomAnswer(RandomAnswerType.NO)
            }
        }
    }

    // TODO: needed?
    get = (id1: string, id2: string): InventoryCombination | undefined =>
        this.loadedCombinations[this.buildInventoryCombinationId(id1, id2)] ?? undefined

    // TODO: needed?
    // removeCombination()
}

export default new InventoryCombinationsController()
